/**
 * Shared typed API client (R-18.95: lives at `shared/api/client.ts`; feature endpoints
 * live in `features/<name>/api/`). Implemented with Axios per R-18.196/R-18.197 (no
 * third-party auth library — auth is an `AuthProvider` + `useAuth` + Axios interceptor;
 * the provider arrives in M9, this milestone ships the client + interceptor).
 *
 * Token transport (R-18.198/R-18.199, DEC-026): the SPA NEVER stores tokens. The M7
 * backend sets access/refresh tokens as HttpOnly cookies, so the browser replays them
 * automatically — `withCredentials: true` opts every request into sending them. The SPA
 * never reads or writes a token anywhere.
 *
 * CSRF (double-submit — services/api/src/auth/cookies.ts): for state-changing requests
 * the client reads the readable `csrf_token` cookie and echoes it into the
 * `X-CSRF-Token` header. Safe methods (GET/HEAD/OPTIONS) carry no CSRF header.
 *
 * 429 replay: a rate-limited request never reached the handler, so the same interceptor
 * waits a short backoff and replays it (see MAX_RATE_LIMIT_RETRIES) instead of failing the
 * call — otherwise an edge-limiter burst silently drops files out of a batch upload.
 *
 * 401 refresh flow (R-18.205/.206/.207/.208): a response interceptor on THIS shared
 * instance catches 401s. A single in-flight refresh is shared across all queued 401s
 * (R-18.206) — concurrent failures await the same refresh promise rather than each
 * firing their own. Retried requests are marked `_retry` and a second 401 on a retried
 * request rejects immediately (R-18.208). On refresh failure the client hard-redirects
 * to `/login` via `window.location.href` to clear all in-memory state (R-18.207) —
 * EXCEPT when already on `/login`, where a reload would just re-probe and loop.
 *
 * Session-probe opt-out: a caller that is merely asking "is the session alive?" (the
 * `GET /auth/me` startup probe) sets `{ meta: { skipAuthRefresh: true } }` on the
 * request config. The interceptor checks this at the TOP of its 401 branch and rejects
 * the 401 straight away — no `runRefresh()`, no redirect — so a logged-out visitor on
 * `/login` does not trigger refresh→401→hard-redirect→reload→re-probe forever. On a
 * probe 401 the caller (`AuthProvider`) then drives ONE explicit
 * {@link attemptSessionRefresh} (also redirect-free) to restore a session whose access
 * cookie merely expired between page loads; only when that fails is the visitor
 * "logged out", and the declarative `<ProtectedRoute>` does the routing.
 */

import axios, { AxiosHeaders } from 'axios';

import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/app/env';
import { describeApiError } from '@/shared/api/error';
import { recordRequest } from '@/shared/diagnostics/request-log';

// Let callers attach our `meta` flag to any request config (e.g. the session probe's
// `{ meta: { skipAuthRefresh: true } }`) in a type-safe way. The augmentation lands on
// `AxiosRequestConfig`, which `InternalAxiosRequestConfig` extends, so both the public
// call site and the interceptor's `original.meta` are typed.
declare module 'axios' {
  interface AxiosRequestConfig {
    meta?: RequestMeta;
  }
}

const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const REFRESH_PATH = '/auth/refresh';
const LOGIN_ROUTE = '/login';
const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * Endpoints where a 401 means "these credentials are wrong", NOT "your session expired".
 * Running the refresh flow on one of these is actively harmful: it replays the current —
 * already-consumed — refresh cookie, the backend correctly reads that as token REUSE
 * (`auth.refresh.reuse`) and revokes the whole token family as a security response. The
 * result is that ONE mistyped password destroys a session that was still perfectly valid,
 * and each retry burns another failed-login attempt toward the account lockout.
 */
const CREDENTIAL_PATHS = ['/auth/login', '/auth/login/verify'];

/** Per-request metadata our interceptors read. `skipAuthRefresh` opts a request out of
 * the 401→refresh→hard-redirect flow (used by the session-alive probe). */
export interface RequestMeta {
  skipAuthRefresh?: boolean;
  /**
   * Hand a 429 straight back instead of waiting it out here. For a caller that owns its own
   * retries (the act's file uploads, `uploadWithRetry`): stacking both would multiply attempts.
   */
  skipRateLimitRetry?: boolean;
}

// Augment the request config with our single-flight retry marker (R-18.208), the
// rate-limit retry counter, and the opt-out meta flag.
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _rateLimitRetries?: number;
  meta?: RequestMeta;
}

// ── 429 → wait → replay ──────────────────────────────────────────────────────
// A 429 means the request NEVER reached the handler (the Nginx edge zone or the app tier
// refused it at the door), so replaying it is safe for every method — nothing was written.
// Letting it through instead is what silently loses data: a batch upload posts one file per
// request, and on 21.08.2026 a 26-photo gallery batch had 9 of its POSTs rejected by the
// EDGE limiter (zone crm_stw_lv_limit, 20 r/s + burst 60) while the operator was told only
// that 17 photos were added.
//
// The backoff is deliberately SHORT and ignores `Retry-After`: both limiters answer with the
// same byte-for-byte envelope (R-5.69) advertising the app tier's 60-second window, but the
// edge zone that actually rejects here is a per-SECOND bucket that refills in well under a
// second. Waiting the advertised minute would stall a photo batch for minutes; a few hundred
// ms recovers the edge case, and a genuine app-tier 429 simply exhausts the attempts and
// surfaces to the caller exactly as it did before.
const MAX_RATE_LIMIT_RETRIES = 3;
const RATE_LIMIT_BACKOFF_MS = [400, 1200, 2500];

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── In-memory CSRF token (DEC-026) ───────────────────────────────────────────
// The backend returns the CSRF token in the login/refresh BODY so the SPA can echo it
// even when the rotated `csrf_token` cookie is not yet readable (Set-Cookie read race).
// It is a FALLBACK only — the readable cookie, when present, wins (see the request
// interceptor): the server's double-submit check compares the header against whatever
// cookie the browser sends, so echoing the current cookie always matches, while this
// per-tab copy goes stale the moment ANOTHER tab's refresh rotates the cookie. Module
// memory only — NEVER localStorage (R-18.199).
let csrfToken: string | null = null;

/** Seed the in-memory CSRF token from a login/refresh response body (DEC-026). */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Read a non-HttpOnly cookie value (the readable CSRF double-submit token). */
function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * Query-param encoder. Axios's default reverts `%20` back to `+`
 * (form-urlencoded style), but the API's OpenAPI request validator treats `+` in a query
 * value as an unencoded reserved character and answers `400 VALIDATION_ERROR ("Parameter
 * search must be url encoded")` — so any server-side search containing a space failed.
 * Keep the rest of axios's readability reverts (`:` `$` `,` `[` `]` stay literal — the
 * comma matters: `?ids=a,b` lists rely on it) but leave spaces as `%20`, which every
 * layer accepts unambiguously.
 */
function encodeQueryParam(value: unknown): string {
  return encodeURIComponent(String(value))
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']');
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  // Send the HttpOnly auth cookies on every request (R-18.198/R-18.199).
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  // `indexes: null` repeats an array param under its own name (`?state=open&state=released`)
  // instead of axios's default `state[]=open`. That is what the API's OpenAPI declares
  // (`style: form, explode: true`) and what its strict Zod query schemas accept — a bracketed
  // key is an UNKNOWN key there and comes back a 400. Every other list joins its arrays with
  // commas (`ids=a,b`), which this does not touch.
  paramsSerializer: { encode: encodeQueryParam, indexes: null },
});

// ── Diagnostics tap (shared/diagnostics/request-log.ts) ──────────────────────
// Every call this SPA makes goes through this one instance, so recording here — and only
// here — captures the whole picture with no per-feature instrumentation. Start times are held
// in a WeakMap keyed by the request config rather than stamped onto `meta`: `meta` is the
// public per-request opt-in bag callers write, and an internal timing field has no business
// in it. A retried request gets a fresh config, so it is timed (and recorded) separately.
const startTimes = new WeakMap<InternalAxiosRequestConfig, number>();

const REQUEST_ID_HEADER = 'x-request-id';

/** Elapsed ms since the request left, or 0 when the config was never stamped. */
function elapsed(config: InternalAxiosRequestConfig | undefined): number {
  const startedAt = config === undefined ? undefined : startTimes.get(config);
  return startedAt === undefined ? 0 : Math.round(performance.now() - startedAt);
}

/** The full request path as issued, so a log line is copy-pasteable into a curl. */
function requestUrl(config: { url?: string; baseURL?: string } | undefined): string {
  if (config?.url === undefined) return '';
  return `${config.baseURL ?? ''}${config.url}`;
}

/** Record a completed (2xx) call — status and timing only; bodies are never captured. */
function recordSuccess(response: AxiosResponse): void {
  const requestId = response.headers?.[REQUEST_ID_HEADER] as string | undefined;
  recordRequest({
    at: new Date().toISOString(),
    method: (response.config.method ?? 'get').toUpperCase(),
    url: requestUrl(response.config),
    status: response.status,
    durationMs: elapsed(response.config as InternalAxiosRequestConfig),
    ...(requestId !== undefined && { requestId }),
    isError: false,
  });
}

/**
 * Record a failed call together with whatever the backend explained. This runs for EVERY
 * failure, including the 401s the refresh flow retries and the network errors that never
 * reached the server — the ones no server-side log can ever show.
 */
function recordFailure(error: AxiosError): void {
  const info = describeApiError(error);
  const headerId = error.response?.headers?.[REQUEST_ID_HEADER] as string | undefined;
  const requestId = info.requestId ?? headerId;
  recordRequest({
    at: new Date().toISOString(),
    method: (error.config?.method ?? 'get').toUpperCase(),
    url: requestUrl(error.config),
    ...(info.status !== undefined && { status: info.status }),
    durationMs: elapsed(error.config as InternalAxiosRequestConfig | undefined),
    ...(requestId !== undefined && { requestId }),
    ...(info.code !== undefined && { code: info.code }),
    // A network failure has no backend message; axios's own text ("Network Error",
    // "timeout of 0ms exceeded") is the only description that exists, so keep it.
    message: info.message ?? error.message,
    ...(info.details !== undefined && { details: info.details }),
    isError: true,
  });
}

// Request interceptor: attach the CSRF double-submit header on state-changing methods.
// The readable `csrf_token` cookie is authoritative: the server's double-submit gate
// compares the header against the cookie the BROWSER sends, and the cookie store is
// shared across tabs — after another tab's refresh rotates the token, this tab's
// in-memory copy is stale and echoing it 403s (which used to hard-logout the tab when
// it hit the silent-refresh POST). The in-memory token seeded from the login/refresh
// body is the fallback for the window where the cookie is not yet readable (DEC-026).
apiClient.interceptors.request.use((config) => {
  startTimes.set(config, performance.now());
  const method = (config.method ?? 'get').toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE) ?? csrfToken;
    if (csrf) {
      const headers = AxiosHeaders.from(config.headers);
      headers.set(CSRF_HEADER, csrf);
      config.headers = headers;
    }
  }
  return config;
});

// ── Single-flight 401 → refresh (R-18.205/.206) ──────────────────────────────
// Only one refresh request is ever in flight; concurrent 401s await this promise.
let refreshPromise: Promise<void> | null = null;

/** The /auth/refresh response body: the rotated double-submit CSRF token (DEC-026). */
interface RefreshResponse {
  csrfToken?: string;
}

function runRefresh(): Promise<void> {
  if (!refreshPromise) {
    // Refresh reads the HttpOnly refresh_token cookie server-side (body optional —
    // see auth.controller refreshSchema); we send no token from JS. The call is flagged
    // `skipAuthRefresh` so its OWN 401 rejects straight out of the interceptor without
    // side effects — each caller decides what a failed refresh means (the 401 retry
    // flow redirects to /login; attemptSessionRefresh() just reports `false`).
    refreshPromise = apiClient
      .post<RefreshResponse>(REFRESH_PATH, {}, { meta: { skipAuthRefresh: true } })
      .then((response) => {
        // Refresh ROTATES the csrf_token (cookies.ts mints a fresh one per rotation) and
        // echoes the new value in the body. Re-seed the in-memory fallback token so the
        // next mutation matches even if the rotated cookie is not yet readable. Mirrors
        // login().
        const rotated = response.data?.csrfToken;
        if (rotated) {
          setCsrfToken(rotated);
        }
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Try one silent cookie-based session refresh, sharing the single-flight promise with
 * the 401 interceptor (R-18.206). Resolves `true` when the session was renewed and
 * `false` when it could not be (no/expired/consumed refresh cookie). It NEVER
 * hard-redirects: the caller — the `/auth/me` startup probe — maps `false` to the
 * logged-out state and the declarative `<ProtectedRoute>` does the routing. This is
 * what keeps a page reload / new tab past the 15-min access-cookie TTL from landing on
 * the login screen while the 14-day refresh cookie is still live.
 */
export async function attemptSessionRefresh(): Promise<boolean> {
  try {
    await runRefresh();
    return true;
  } catch {
    return false;
  }
}

/**
 * The hard navigation to the login page, as a seam: jsdom cannot navigate, so tests replace `assign`
 * to observe WHETHER the client bounced (the act uploads' aborted-request rule depends on it).
 */
export const loginNavigation = {
  assign(path: string): void {
    window.location.href = path;
  },
};

function redirectToLogin(): void {
  // Hard redirect clears all in-memory state (R-18.207). Guard against the reload loop:
  // when ALREADY on /login a hard navigation would remount, re-probe, 401 again, and
  // bounce here forever. Skip it — the declarative <ProtectedRoute>/login screen already
  // renders the correct logged-out UI without a full-page reload.
  if (window.location.pathname === LOGIN_ROUTE) {
    return;
  }
  loginNavigation.assign(LOGIN_ROUTE);
}

apiClient.interceptors.response.use(
  (response) => {
    recordSuccess(response);
    return response;
  },
  async (error: AxiosError) => {
    // Record BEFORE the refresh/retry logic below: a 401 that is silently refreshed and
    // replayed still happened, and a session that expires mid-action is exactly the kind of
    // "it just logged me out" report this buffer exists to explain.
    recordFailure(error);

    const original = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    // Refused at the door (429): wait out the bucket and replay, up to a few attempts.
    if (status === 429 && original && original.meta?.skipRateLimitRetry !== true) {
      const attempt = original._rateLimitRetries ?? 0;
      const backoffMs = RATE_LIMIT_BACKOFF_MS[attempt];
      if (attempt < MAX_RATE_LIMIT_RETRIES && backoffMs !== undefined) {
        original._rateLimitRetries = attempt + 1;
        await wait(backoffMs);
        return apiClient(original as AxiosRequestConfig);
      }
      return Promise.reject(error);
    }

    // Not a 401, or no config to retry: propagate as-is.
    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    // A 401 from a CREDENTIAL endpoint is "wrong password", not "expired session" — hand it
    // straight back to the login form. Refreshing here would replay the consumed refresh
    // cookie, which the backend classifies as token reuse and answers by revoking the whole
    // family (see CREDENTIAL_PATHS).
    const requestPath = original.url ?? '';
    if (CREDENTIAL_PATHS.some((path) => requestPath.includes(path))) {
      return Promise.reject(error);
    }

    // Session-probe opt-out (TOP of the 401 branch): a request flagged
    // `skipAuthRefresh` is just asking "is the session alive?" — a 401 means "no", so
    // reject immediately WITHOUT running refresh or redirecting. This is what stops the
    // logged-out probe→refresh→401→hard-redirect→reload→re-probe loop on /login.
    if (original.meta?.skipAuthRefresh === true) {
      return Promise.reject(error);
    }

    // A 401 from the refresh endpoint itself, or a 401 on an already-retried request,
    // is terminal (R-18.208): do not loop — clear state and bounce to /login.
    const isRefreshCall = (original.url ?? '').includes(REFRESH_PATH);
    if (original._retry || isRefreshCall) {
      // The session is gone; drop any seeded CSRF token so a stale value is not echoed.
      setCsrfToken(null);
      redirectToLogin();
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      await runRefresh();
    } catch (refreshError) {
      // A request its caller already gave up on (an upload's stall watchdog aborted it while it waited
      // here) must not bounce the page: the caller has moved on, and a hard redirect would throw away
      // whatever the operator is doing now. A refresh that failed for real still bounces through the
      // requests that are alive.
      if (original.signal?.aborted === true) {
        return Promise.reject(refreshError);
      }
      // The session is gone; drop the seeded CSRF fallback so a stale value is not
      // echoed after the bounce (runRefresh's own 401 skips the isRefreshCall branch
      // above via its `skipAuthRefresh` flag, so the cleanup lives here).
      setCsrfToken(null);
      redirectToLogin();
      return Promise.reject(refreshError);
    }

    // Refresh rotated the cookies; replay the original request once.
    return apiClient(original as AxiosRequestConfig);
  },
);
